import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

export interface ApexoDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  width?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  hasBackdrop?: boolean;
}

export default function ApexoDrawer({ 
  isOpen, 
  onClose, 
  title, 
  width = '360px', 
  children, 
  footer,
  hasBackdrop = true
}: ApexoDrawerProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen && hasBackdrop) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, hasBackdrop]);

  if (!mounted) return null;

  const drawerContent = (
    <div className={`apexo-drawer-root ${isOpen ? 'open' : ''}`} style={!hasBackdrop ? { pointerEvents: 'none' } : {}}>
      {hasBackdrop && <div className="apexo-drawer-backdrop" onClick={onClose} />}
      
      <div 
        className="apexo-drawer-panel" 
        style={{ width, transform: isOpen ? 'translateX(0)' : 'translateX(100%)', pointerEvents: 'auto' }}
      >
        <div className="apexo-drawer-header">
          {title && <h3 className="apexo-drawer-title">{title}</h3>}
          <button className="apexo-drawer-close" onClick={onClose}>
            ✕
          </button>
        </div>
        
        <div className="apexo-drawer-body">
          {children}
        </div>
        
        {footer && (
          <div className="apexo-drawer-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(drawerContent, document.body);
}
